import React, { useState, useEffect, useMemo } from "react";

// ─── CONFIG ──────────────────────────────────────────────────────────────────

const CONFIG = {
  SHEET_ID: "1GDnWvillWSoU7gku4F63rdhi93Uk2GEPb2nJj9s7OW8",
  API_KEY: process.env.REACT_APP_SHEETS_API_KEY,
  TABS: {
    scorecard: "Dashboard Score Card",
    costs:     "Dashboard Clean Project Costs",
  },
  CLIENT: {
    name: "TwelveOne",
  },
};

const CURRENT_YEAR = new Date().getFullYear().toString();

const LIGHT = {
  bg: "#EFEFEF", bgCard: "#E4E0D6", black: "#0A0B0D",
  orange: "#E67E50", orangeTint: "#F9E4D8", green: "#5F7F74",
  muted: "#4A4A46", faint: "#D4D0C6", text: "#0A0B0D",
};

const DARK = {
  bg: "#0A0B0D", bgCard: "#1E1E1B", black: "#F5F1E8",
  orange: "#E67E50", orangeTint: "#2A1810", green: "#5F7F74",
  muted: "#7A7A75", faint: "#232320", text: "#F5F1E8",
};

const mono = "'IBM Plex Mono', monospace";
const sans = "'DM Sans', sans-serif";

const FONTS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap');
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const STATUS_COLOR = {
  "Fully Funded":   "green",
  "On Track":       "green",
  "Early Cycle":    "muted",
  "Lower Margin":   "orange",
  "Check In":       "orange",
  "Higher Margin":  "green",
  "Healthy Margin": "green",
};

const SCORECARD_COLS = [
  { label: "Project",      w: "18%", key: "Project_Key" },
  { label: "Event Date",   w: "7%",  key: "Event_Date" },
  { label: "Days Out",     w: "5%",  key: "Days_To_Event" },
  { label: "Package",      w: "7%",  key: "Package_Price" },
  { label: "Collected",    w: "7%",  key: "Total_Revenue" },
  { label: "Costs",        w: "7%",  key: "Total_Costs" },
  { label: "Proc. Fees",   w: "7%",  key: "Processing_Fees" },
  { label: "Proj. Margin", w: "7%",  key: "Projected_Margin" },
  { label: "Act. Margin",  w: "7%",  key: "Actual_Margin" },
  { label: "Status",       w: "12%", key: "Funding_Status" },
  { label: "Outstanding",  w: "10%", key: "Outstanding_Balance" },
];

const COST_COLS = [
  { label: "Project",     w: "13%", key: "Project_Key" },
  { label: "Vendor",      w: "12%", key: "Entity" },
  { label: "Description", w: "24%", key: "Description" },
  { label: "Date",        w: "9%",  key: "TxnDate" },
  { label: "Status",      w: "8%",  key: "Payment_Status" },
  { label: "Account",     w: "24%", key: "Account_Name" },
  { label: "Amount",      w: "10%", key: "Amount" },
];

async function fetchSheet(tabName, retries = 3) {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${CONFIG.SHEET_ID}/values/${encodeURIComponent(tabName)}?key=${CONFIG.API_KEY}`;
  for (let i = 0; i < retries; i++) {
    const res = await fetch(url);
    if (res.ok) {
      const data = await res.json();
      const [headers, ...rows] = data.values || [];
      return rows
        .filter(row => row.some(cell => cell !== ""))
        .map(row => headers.reduce((obj, h, idx) => { obj[h] = row[idx] ?? ""; return obj; }, {}));
    }
    if (i < retries - 1) await new Promise(r => setTimeout(r, 1000));
  }
  throw new Error(`Failed to fetch ${tabName}`);
}

const n = v => parseFloat(String(v).replace(/[$,]/g, "")) || 0;

const fmtK = v => {
  const num = n(v);
  if (num === 0) return "—";
  const abs = Math.abs(num);
  const fmt = abs >= 1000 ? `$${(abs / 1000).toFixed(1)}K` : `$${abs.toFixed(0)}`;
  return num < 0 ? `−${fmt}` : fmt;
};

const fmtFull = v => {
  const num = n(v);
  const abs = Math.abs(num);
  const fmt = `$${abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return num < 0 ? `−${fmt}` : fmt;
};

function sortData(data, col, dir) {
  if (!col) return data;
  return [...data].sort((a, b) => {
    if (col === "TxnDate" || col === "Event_Date") {
      const ad = new Date(a[col] || 0), bd = new Date(b[col] || 0);
      return dir === "asc" ? ad - bd : bd - ad;
    }
    const aNum = parseFloat(a[col]), bNum = parseFloat(b[col]);
    if (!isNaN(aNum) && !isNaN(bNum)) return dir === "asc" ? aNum - bNum : bNum - aNum;
    if (!isNaN(aNum)) return -1;
    if (!isNaN(bNum)) return 1;
    return dir === "asc"
      ? String(a[col] || "").localeCompare(String(b[col] || ""))
      : String(b[col] || "").localeCompare(String(a[col] || ""));
  });
}

const rowId = c =>
  `${c.Project_Key || ""}|${c.TxnDate || ""}|${c.Amount || ""}|${c.Description || ""}|${c.Entity || ""}`;

function LoadingScreen() {
  return (
    <div style={{ background: "#0A0B0D", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{FONTS}</style>
      <div style={{ fontFamily: mono, fontSize: "10px", color: "#7A7A75", letterSpacing: "0.15em" }}>Loading {CONFIG.CLIENT.name} data...</div>
    </div>
  );
}

function PasswordGate({ onUnlock }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const attempt = () => {
    if (input === process.env.REACT_APP_DASHBOARD_PASSWORD) {
      localStorage.setItem("ikifi_auth_twelveone", "true");
      setLoading(true);
      setTimeout(() => onUnlock(), 2000);
    } else {
      setError(true);
      setTimeout(() => setError(false), 1500);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ background: "#0A0B0D", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "32px" }}>
      <style>{FONTS}</style>
      <div style={{ display: "flex", alignItems: "baseline" }}>
        <span style={{ fontFamily: sans, fontWeight: 700, fontSize: "64px", color: "#F5F1E8", letterSpacing: "-0.02em" }}>ikifi</span>
        <span style={{ fontFamily: sans, fontWeight: 700, fontSize: "78px", color: "#E67E50" }}>.</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "300px" }}>
        <div style={{ position: "relative" }}>
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Enter password"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && attempt()}
            style={{ fontFamily: mono, fontSize: "11px", letterSpacing: "0.08em", padding: "12px 48px 12px 16px", background: "#161614", border: `1px solid ${error ? "#E67E50" : "#232320"}`, color: "#F5F1E8", outline: "none", width: "100%", boxSizing: "border-box", transition: "border-color 0.2s" }}
          />
          <button onClick={() => setShowPassword(p => !p)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", fontFamily: mono, fontSize: "9px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7A7A75", background: "none", border: "none", cursor: "pointer" }}>
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
        <button onClick={attempt} style={{ fontFamily: mono, fontSize: "10px", letterSpacing: "0.12em", textTransform: "uppercase", padding: "12px", background: "#E67E50", color: "#fff", border: "none", cursor: "pointer" }}>Enter</button>
        {error && <div style={{ fontFamily: mono, fontSize: "9px", color: "#E67E50", letterSpacing: "0.08em", textAlign: "center" }}>Incorrect password</div>}
      </div>
    </div>
  );
}

function Logo({ t, size = 19 }) {
  return (
    <a href="https://ikifi.co" target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "baseline", textDecoration: "none" }}>
      <span style={{ fontFamily: sans, fontWeight: 700, fontSize: `${size}px`, color: t.black, letterSpacing: "-0.02em" }}>ikifi</span>
      <span style={{ fontFamily: sans, fontWeight: 700, fontSize: `${size * 1.15}px`, color: t.orange }}>.</span>
    </a>
  );
}

function KPICard({ label, value, sub, alert, positive, accentBg, t, mobile }) {
  const bg    = alert ? t.orange : accentBg ? t.green : t.bgCard;
  const col   = alert || accentBg ? "#fff" : t.black;
  const scol  = alert || accentBg ? "rgba(255,255,255,0.75)" : t.muted;
  const barColor = alert ? t.orange : accentBg ? t.green : positive === true ? t.green : positive === false ? t.orange : t.bgCard;
  return (
    <div
      style={{ flex: mobile ? "none" : 1, width: mobile ? "100%" : undefined, minWidth: 0, background: bg, display: "flex", flexDirection: "column", transition: "transform 0.15s, box-shadow 0.15s", cursor: "default" }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
    >
      <div style={{ height: "3px", background: barColor }} />
      <div style={{ padding: mobile ? "20px 24px" : "18px 22px", display: "flex", flexDirection: "column", gap: "10px" }}>
        <div style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.13em", textTransform: "uppercase", color: scol }}>{label}</div>
        <div style={{ fontFamily: mono, fontSize: mobile ? "28px" : "22px", fontWeight: 500, color: col, letterSpacing: "-0.03em", lineHeight: 1 }}>{value}</div>
        <div style={{ fontFamily: mono, fontSize: "9px", color: scol, minHeight: "12px" }}>{sub || ""}</div>
      </div>
    </div>
  );
}

function SLabel({ children, t, noMargin }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: noMargin ? 0 : "14px" }}>
      <span style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.14em", color: t.muted, textTransform: "uppercase", whiteSpace: "nowrap" }}>{children}</span>
      <div style={{ flex: 1, height: "1px", background: t.faint }} />
    </div>
  );
}

function ResetSort({ show, onClick, t }) {
  if (!show) return null;
  return (
    <button onClick={onClick} style={{ fontFamily: mono, fontSize: "9px", color: t.muted, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
      Reset Sort
    </button>
  );
}

function Sel({ value, onChange, options, placeholder, optionLabels = {}, t }) {
  const [open, setOpen] = useState(false);
  const [selSearch, setSelSearch] = useState("");
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handle = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSelSearch(""); } };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const selected = value ? (optionLabels[value] || value) : placeholder;
  const filtered = options.filter(o => (optionLabels[o] || o).toLowerCase().includes(selSearch.toLowerCase()));

  return (
    <div ref={ref} style={{ position: "relative", minWidth: "190px", userSelect: "none" }}>
      <div onClick={() => { setOpen(o => !o); setSelSearch(""); }} tabIndex={0}
        style={{ fontFamily: mono, fontSize: "10px", letterSpacing: "0.06em", color: value ? t.text : t.muted, background: t.bgCard, border: `1px solid ${t.faint}`, padding: "8px 14px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", outline: "none" }}>
        <span>{selSearch || selected}</span>
        <span style={{ fontSize: "8px", color: t.muted, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>▼</span>
      </div>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: t.bgCard, border: `1px solid ${t.faint}`, borderTop: "none", maxHeight: "260px", overflowY: "auto" }}>
          <div onClick={() => { onChange(""); setSelSearch(""); setOpen(false); }}
            style={{ fontFamily: mono, fontSize: "10px", letterSpacing: "0.06em", padding: "8px 14px", cursor: "pointer", color: !value ? t.orange : t.muted, background: !value ? `${t.orange}10` : "transparent", borderBottom: `1px solid ${t.faint}` }}>
            {placeholder}
          </div>
          {filtered.map(o => (
            <div key={o} onClick={() => { onChange(o); setSelSearch(""); setOpen(false); }}
              style={{ fontFamily: mono, fontSize: "10px", letterSpacing: "0.06em", padding: "8px 14px", cursor: "pointer", color: value === o ? t.orange : t.text, background: value === o ? `${t.orange}10` : "transparent", borderBottom: `1px solid ${t.faint}`, transition: "background 0.1s" }}
              onMouseEnter={e => { if (value !== o) e.currentTarget.style.background = t.faint; }}
              onMouseLeave={e => { if (value !== o) e.currentTarget.style.background = "transparent"; }}>
              {optionLabels[o] || o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScrollTable({ headers, rows, footer, t, maxHeight = "55vh", sortCol, sortDir, onSort, dark }) {
  return (
    <div style={{ border: `1px solid ${t.faint}`, overflow: "hidden" }}>
      <div style={{ display: "flex", padding: "11px 16px", background: dark ? "#1E1E1C" : t.black, borderBottom: `1px solid ${t.faint}`, borderLeft: "4px solid transparent" }}>
        {headers.map((h, i) => (
          <div key={i} onClick={() => h.key && onSort(h.key)}
            style={{ width: h.w, flexShrink: 0, fontFamily: mono, fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", color: sortCol === h.key ? t.orange : "#9B9891", cursor: h.key ? "pointer" : "default", display: "flex", alignItems: "center", gap: "4px", userSelect: "none" }}>
            {h.label}
            {h.key && <span style={{ opacity: sortCol === h.key ? 1 : 0.3, fontSize: "8px" }}>{sortCol === h.key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>}
          </div>
        ))}
      </div>
      <div style={{ maxHeight, overflowY: "auto" }}>{rows}</div>
      {footer && <div style={{ borderTop: `1px solid ${t.faint}`, background: t.bgCard }}>{footer}</div>}
    </div>
  );
}

function NextEventCard({ t, events, nextDays, mobile }) {
  const [idx, setIdx] = useState(0);
  const touchStartX = React.useRef(null);

  const handleTouchStart = e => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = e => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) setIdx(i => Math.min(i + 1, events.length - 1));
      else setIdx(i => Math.max(i - 1, 0));
    }
    touchStartX.current = null;
  };

  const current = events[idx];
  return (
    <div
      style={{ flex: mobile ? "none" : 1, width: mobile ? "100%" : undefined, minWidth: 0, background: t.bgCard, display: "flex", flexDirection: "column", cursor: "default" }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div style={{ height: "3px", background: t.bgCard }} />
      <div style={{ padding: mobile ? "20px 24px" : "18px 22px", display: "flex", flexDirection: "column", gap: "10px" }}>
        <div style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.13em", textTransform: "uppercase", color: t.muted, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Next Event</span>
          {events.length > 1 && (
            <div style={{ display: "flex", gap: "4px" }}>
              {events.map((_, i) => (
                <div key={i} onClick={() => setIdx(i)} style={{ width: "5px", height: "5px", borderRadius: "50%", background: i === idx ? t.orange : t.faint, cursor: "pointer", transition: "background 0.2s" }} />
              ))}
            </div>
          )}
        </div>
        <div style={{ fontFamily: mono, fontSize: mobile ? "28px" : "22px", fontWeight: 500, color: t.black, letterSpacing: "-0.03em", lineHeight: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {current ? current.Project_Key.split("|")[0].trim() : "—"}
        </div>
        <div style={{ fontFamily: mono, fontSize: "9px", color: t.muted, minHeight: "12px" }}>
          {nextDays ? `${nextDays} days out` : "no upcoming events"}{events.length > 1 ? ` · ${events.length} events` : ""}
        </div>
      </div>
    </div>
  );
}

// ─── 60-DAY MARGIN BAR ───────────────────────────────────────────────────────

function MarginBar({ p, t }) {
  const [tooltip, setTooltip] = useState(null);

  const collected   = n(p.Total_Revenue);
  const costs       = n(p.Total_Costs);
  const margin      = n(p.Projected_Margin);
  const pkg         = n(p.Package_Price);
  const daysOut     = n(p.Days_To_Event);
  const outstanding = n(p.Outstanding_Balance);

  const collectedPct = pkg > 0 ? Math.min((collected / pkg) * 100, 100) : 0;
  const costsPct     = pkg > 0 ? Math.min((costs / pkg) * 100, 100) : 0;
  const marginPct    = pkg > 0 ? Math.max((margin / pkg) * 100, 0) : 0;

  const segments = [
    { key: "collected", pct: collectedPct, color: t.green,  label: "Collected",    amt: collected },
    { key: "costs",     pct: costsPct,     color: t.orange, label: "Costs",        amt: costs },
    { key: "margin",    pct: marginPct,    color: t.black,  label: "Proj. Margin", amt: margin },
  ];

  return (
    <div style={{ marginBottom: "22px" }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: "7px" }}>
        <span style={{ fontFamily: sans, fontSize: "12px", fontWeight: 600, color: t.black }}>{p.Project_Key}</span>
        <span style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.08em", color: daysOut <= 30 ? t.orange : t.muted, fontWeight: daysOut <= 30 ? 600 : 400, marginLeft: "10px" }}>{daysOut}d out</span>
      </div>
      <div style={{ position: "relative", height: "24px", overflow: "visible" }}>
        <div style={{ position: "absolute", top: 0, bottom: 0, width: "100%", background: t.faint }} />
        {outstanding > 0 && (
          <div
            style={{
              position: "absolute", top: 0, bottom: 0,
              left: `${collectedPct}%`,
              width: `${Math.min((outstanding / pkg) * 100, 100 - collectedPct)}%`,
              backgroundImage: `radial-gradient(circle, ${t.orange} 1.5px, transparent 1.5px)`,
              backgroundSize: "6px 6px",
              cursor: "pointer",
            }}
            onMouseEnter={() => setTooltip({ key: "outstanding", label: "Outstanding", amt: outstanding })}
            onMouseLeave={() => setTooltip(null)}
          />
        )}
        {segments.map(s => (
          <div
            key={s.key}
            style={{
              position: "absolute", top: 0, bottom: 0,
              width: `${s.pct}%`,
              background: s.color,
              transition: "transform 0.15s",
              cursor: "pointer",
            }}
            onMouseEnter={e => {
              setTooltip({ key: s.key, label: s.label, amt: s.amt });
              e.currentTarget.style.transform = "scaleY(1.25)";
              e.currentTarget.style.transformOrigin = "center";
            }}
            onMouseLeave={e => {
              setTooltip(null);
              e.currentTarget.style.transform = "scaleY(1)";
            }}
          />
        ))}
        {tooltip && (
          <div style={{
            position: "absolute", bottom: "32px", left: "50%", transform: "translateX(-50%)",
            background: t.black, color: t.bg,
            fontFamily: mono, fontSize: "10px", padding: "5px 12px",
            whiteSpace: "nowrap", pointerEvents: "none", zIndex: 10,
          }}>
            <span style={{ color: tooltip.key === "margin" && n(tooltip.amt) < 2000 ? t.orange : "inherit" }}>
              {tooltip.label} · {fmtFull(tooltip.amt)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── APP ─────────────────────────────────────────────────────────────────────

export default function App() {
  const [dark, setDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
  const [tab, setTab] = useState("overview");
  const [hoveredRow, setHoveredRow] = useState(null);
  const [projFilter, setProjFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scorecard, setScorecard] = useState([]);
  const [costs, setCosts] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [reconcileMode, setReconcileMode] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [reconciled, setReconciled] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("ikifi_tw_reconciled") || "[]")); }
    catch { return new Set(); }
  });

  const toggleReconciled = id => {
    setReconciled(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      localStorage.setItem("ikifi_tw_reconciled", JSON.stringify([...next]));
      return next;
    });
  };

  React.useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  const [sSortCol, setSSortCol] = useState(() => { const s = localStorage.getItem("ikifi_tw_scoreSort"); return s ? JSON.parse(s).col : "Days_To_Event"; });
  const [sSortDir, setSSortDir] = useState(() => { const s = localStorage.getItem("ikifi_tw_scoreSort"); return s ? JSON.parse(s).dir : "asc"; });
  const [cSortCol, setCSortCol] = useState(() => { const s = localStorage.getItem("ikifi_tw_costSort"); return s ? JSON.parse(s).col : "TxnDate"; });
  const [cSortDir, setCSortDir] = useState(() => { const s = localStorage.getItem("ikifi_tw_costSort"); return s ? JSON.parse(s).dir : "desc"; });

  React.useEffect(() => { if (sSortCol) localStorage.setItem("ikifi_tw_scoreSort", JSON.stringify({ col: sSortCol, dir: sSortDir })); }, [sSortCol, sSortDir]);
  React.useEffect(() => { if (cSortCol) localStorage.setItem("ikifi_tw_costSort", JSON.stringify({ col: cSortCol, dir: cSortDir })); }, [cSortCol, cSortDir]);

  const [unlocked, setUnlocked] = useState(false);
  const [gateLoading, setGateLoading] = useState(localStorage.getItem("ikifi_auth_twelveone") === "true");

  React.useEffect(() => {
    if (localStorage.getItem("ikifi_auth_twelveone") === "true") {
      setTimeout(() => { setUnlocked(true); setGateLoading(false); }, 2000);
    } else {
      setGateLoading(false);
    }
  }, []);

  const t = dark ? DARK : LIGHT;

  const handleScoreSort = key => { if (sSortCol === key) setSSortDir(d => d === "asc" ? "desc" : "asc"); else { setSSortCol(key); setSSortDir("asc"); } };
  const handleCostSort  = key => { if (cSortCol === key) setCSortDir(d => d === "asc" ? "desc" : "asc"); else { setCSortCol(key); setCSortDir("asc"); } };
  const resetScoreSort  = () => { setSSortCol("Days_To_Event"); setSSortDir("asc"); localStorage.removeItem("ikifi_tw_scoreSort"); };
  const resetCostSort   = () => { setCSortCol("TxnDate"); setCSortDir("desc"); localStorage.removeItem("ikifi_tw_costSort"); };

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const [sc, co] = await Promise.all([fetchSheet(CONFIG.TABS.scorecard), fetchSheet(CONFIG.TABS.costs)]);
        setScorecard(sc);
        setCosts(co);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filteredScorecard = useMemo(() => scorecard.filter(p => p.Project_Key && p.Project_Key !== ""), [scorecard]);
  const sortedScorecard   = useMemo(() => sortData(filteredScorecard, sSortCol, sSortDir), [filteredScorecard, sSortCol, sSortDir]);

  // ─── SPLIT UPCOMING / COMPLETED ────────────────────────────────────────────
  const upcomingEvents  = useMemo(() => sortedScorecard.filter(p => n(p.Days_To_Event) >= 0), [sortedScorecard]);
  const completedEvents = useMemo(() => sortedScorecard.filter(p => n(p.Days_To_Event) < 0), [sortedScorecard]);

  const kpis = useMemo(() => {
    const totalEvents       = filteredScorecard.filter(p => n(p.Days_To_Event) >= 0).length;
    const packagesSold      = filteredScorecard.filter(p => n(p.Days_To_Event) >= 0).reduce((s, p) => s + n(p.Package_Price), 0);
    const totalCollected    = filteredScorecard.filter(p => n(p.Days_To_Event) >= 0).reduce((s, p) => s + n(p.Total_Revenue), 0);
    const totalCosts        = filteredScorecard.filter(p => n(p.Days_To_Event) >= 0).reduce((s, p) => s + n(p.Total_Costs), 0);
    const eventsAtRisk      = filteredScorecard.filter(p => n(p.Days_To_Event) >= 0 && n(p.Total_Costs) > 0 && n(p.Projected_Margin) < 2000).length;
    const projMarginTotal   = filteredScorecard.filter(p => n(p.Days_To_Event) >= 0 && n(p.Total_Costs) > 0).reduce((s, p) => s + n(p.Projected_Margin), 0);
    const actualMarginTotal = filteredScorecard.filter(p => n(p.Days_To_Event) < 0 && p.Actual_Margin !== "" && p.Actual_Margin !== undefined).reduce((s, p) => s + n(p.Actual_Margin), 0);
    const upcoming          = [...filteredScorecard].filter(p => n(p.Days_To_Event) > 0).sort((a, b) => n(a.Days_To_Event) - n(b.Days_To_Event));
    const nextDays          = upcoming.length > 0 ? n(upcoming[0].Days_To_Event) : null;
    const nextEvents        = upcoming.filter(p => n(p.Days_To_Event) === nextDays);
    return { totalEvents, packagesSold, totalCollected, totalCosts, eventsAtRisk, projMarginTotal, actualMarginTotal, nextEvents, nextDays };
  }, [filteredScorecard]);

  const events60 = useMemo(() =>
    filteredScorecard
      .filter(p => n(p.Days_To_Event) > 0 && n(p.Days_To_Event) <= 60)
      .sort((a, b) => n(a.Days_To_Event) - n(b.Days_To_Event)),
    [filteredScorecard]
  );

  const projOptions = [...new Set(costs.map(c => c.Project_Key))].filter(v => v && v !== "#N/A" && v !== "N/A").sort();

  const filteredCosts = useMemo(() =>
    costs.filter(c =>
      c.Project_Key && c.Project_Key !== "#N/A" && c.Project_Key !== "Project_Key" &&
      (!projFilter || c.Project_Key === projFilter) &&
      (!search || [c.Entity, c.Description, c.Account_Name].some(v => v && v.toLowerCase().includes(search.toLowerCase())))
    ), [costs, projFilter, search]
  );

  const sortedCosts = useMemo(() => sortData(filteredCosts, cSortCol, cSortDir), [filteredCosts, cSortCol, cSortDir]);

  // ─── RECONCILE STATS ────────────────────────────────────────────────────────
  const totalCostAmt      = useMemo(() => sortedCosts.reduce((s, c) => s + n(c.Amount || 0), 0), [sortedCosts]);
  const reconciledTotal   = useMemo(() => sortedCosts.filter(c => reconciled.has(rowId(c))).reduce((s, c) => s + n(c.Amount || 0), 0), [sortedCosts, reconciled]);
  const unreconciledTotal = useMemo(() => sortedCosts.filter(c => !reconciled.has(rowId(c))).reduce((s, c) => s + n(c.Amount || 0), 0), [sortedCosts, reconciled]);
  const reconciledCount   = useMemo(() => sortedCosts.filter(c => reconciled.has(rowId(c))).length, [sortedCosts, reconciled]);
  const progressPct       = totalCostAmt > 0 ? (reconciledTotal / totalCostAmt) * 100 : 0;

  // ─── ACCOUNT GROUPS (for reconcile mode) ───────────────────────────────────
  const accountGroups = useMemo(() => {
    if (!reconcileMode) return [];
    const groups = {};
    sortedCosts.forEach(c => {
      const acct = c.Account_Name || "Uncategorized";
      if (!groups[acct]) groups[acct] = [];
      groups[acct].push(c);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [sortedCosts, reconcileMode]);

  const exportCSV = () => {
    const headers = ["Project", "Vendor", "Description", "Date", "Account", "Amount", "Reconciled"];
    const rows = sortedCosts.map(c => [c.Project_Key || "—", c.Entity || "", c.Description || "", c.TxnDate || "", c.Account_Name || "", c.Amount || "", reconciled.has(rowId(c)) ? "Yes" : "No"]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })), download: "twelveone-costs.csv" }).click();
  };

  const signOut = () => { localStorage.removeItem("ikifi_auth_twelveone"); setUnlocked(false); };

  if (gateLoading) return <LoadingScreen />;
  if (!unlocked)   return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  if (loading) return (
    <div style={{ background: "#0A0B0D", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{FONTS}</style>
      <div style={{ fontFamily: mono, fontSize: "10px", color: "#7A7A75", letterSpacing: "0.15em" }}>Loading {CONFIG.CLIENT.name} data...</div>
    </div>
  );

  if (error) return (
    <div style={{ background: t.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "12px" }}>
      <style>{FONTS}</style>
      <div style={{ fontFamily: mono, fontSize: "11px", color: t.orange }}>Dashboard error.</div>
      <div style={{ fontFamily: mono, fontSize: "10px", color: t.muted }}>Contact <a href="mailto:jordan@ikifi.co" style={{ color: t.orange, textDecoration: "none" }}>jordan@ikifi.co</a></div>
    </div>
  );

  // ─── MOBILE VIEW ───────────────────────────────────────────────────────────

  if (isMobile) {
    return (
      <div style={{ background: t.bg, minHeight: "100vh", fontFamily: sans, animation: "fadeIn 0.6s ease-in" }}>
        <style>{FONTS}</style>
        <nav style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${t.faint}`, background: t.bg }}>
          <Logo t={t} size={22} />
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button onClick={() => setDark(d => !d)} style={{ width: "34px", height: "18px", background: dark ? t.orange : t.faint, border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
              <div style={{ position: "absolute", top: "2px", width: "14px", height: "14px", left: dark ? "18px" : "2px", transition: "left 0.2s", background: dark ? "#fff" : t.black }} />
            </button>
            <button onClick={signOut} style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", padding: "7px 14px", background: "transparent", color: t.muted, border: `1px solid ${t.faint}`, cursor: "pointer" }}>
              Sign Out
            </button>
          </div>
        </nav>
        <div style={{ padding: "24px 20px 48px", display: "flex", flexDirection: "column", gap: "2px" }}>
          <div style={{ fontFamily: mono, fontSize: "8px", letterSpacing: "0.14em", color: t.muted, textTransform: "uppercase", marginBottom: "12px" }}>
            {CONFIG.CLIENT.name} · FY{CURRENT_YEAR}
          </div>
          <KPICard t={t} mobile label="Packages Sold"        value={fmtK(kpis.packagesSold)}      sub={`${kpis.totalEvents} events`} />
          <KPICard t={t} mobile label="Revenue Collected"     value={fmtK(kpis.totalCollected)}    sub="total collected to date" positive={true} />
          <KPICard t={t} mobile label="Proj. Margin"          value={fmtK(kpis.projMarginTotal)}   sub="across upcoming events" positive={true} />
          <KPICard t={t} mobile label="Lower Expected Margin" value={kpis.eventsAtRisk}            sub="upcoming below $2K margin" alert={kpis.eventsAtRisk > 0} />
          <NextEventCard t={t} mobile events={kpis.nextEvents || []} nextDays={kpis.nextDays} />
        </div>
        <div style={{ padding: "0 20px 32px", borderTop: `1px solid ${t.faint}`, paddingTop: "20px", display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontFamily: mono, fontSize: "9px", color: t.muted }}>© ikifi {CURRENT_YEAR}</span>
          <span style={{ fontFamily: mono, fontSize: "9px", color: t.muted }}>Clarity, by design.</span>
        </div>
      </div>
    );
  }

  // ─── SCORECARD ROW BUILDER ─────────────────────────────────────────────────

  const buildScorecardRows = (rows, keyPrefix, isCompleted = false) =>
    rows.map((p, i) => {
      const status       = isCompleted ? "Complete" : (p.Funding_Status || "");
      const statusCol    = isCompleted ? t.green : STATUS_COLOR[status] === "green" ? t.green : STATUS_COLOR[status] === "orange" ? t.orange : t.muted;
      const daysOut      = n(p.Days_To_Event);
      const lb           = isCompleted ? t.green : STATUS_COLOR[status] === "green" ? t.green : STATUS_COLOR[status] === "orange" ? t.orange : t.faint;
      const rowKey       = `${keyPrefix}-${i}`;
      const rowBg        = hoveredRow === rowKey ? t.bgCard : i % 2 === 0 ? t.bg : "transparent";
      const actualMargin = p.Actual_Margin !== "" && p.Actual_Margin !== undefined ? n(p.Actual_Margin) : null;
      const procFees     = p.Processing_Fees !== "" && p.Processing_Fees !== undefined ? n(p.Processing_Fees) : null;

      return (
        <div key={rowKey}
          onMouseEnter={() => setHoveredRow(rowKey)}
          onMouseLeave={() => setHoveredRow(null)}
          style={{ display: "flex", alignItems: "center", padding: "11px 16px", borderBottom: `1px solid ${t.faint}`, borderLeft: `4px solid ${lb}`, background: rowBg, transition: "background 0.15s" }}
        >
          {/* Project */}
          <div style={{ width: SCORECARD_COLS[0].w, flexShrink: 0, fontFamily: sans, fontSize: "12px", fontWeight: 600, color: t.black, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "10px" }}>{p.Project_Key}</div>
          {/* Event Date */}
          <div style={{ width: SCORECARD_COLS[1].w, flexShrink: 0, fontFamily: mono, fontSize: "10px", color: t.muted }}>{p.Event_Date || "—"}</div>
          {/* Days Out */}
          <div style={{ width: SCORECARD_COLS[2].w, flexShrink: 0, fontFamily: mono, fontSize: "11px", color: daysOut <= 60 && daysOut >= 0 ? t.orange : t.muted, fontWeight: daysOut <= 60 && daysOut >= 0 ? 700 : 400 }}>
            {daysOut > 0 ? `${daysOut}d` : isCompleted ? "Past" : "—"}
          </div>
          {/* Package */}
          <div style={{ width: SCORECARD_COLS[3].w, flexShrink: 0, fontFamily: mono, fontSize: "11px", color: t.black }}>{n(p.Package_Price) ? fmtK(p.Package_Price) : "—"}</div>
          {/* Collected */}
          <div style={{ width: SCORECARD_COLS[4].w, flexShrink: 0, fontFamily: mono, fontSize: "11px", color: t.black }}>{n(p.Total_Revenue) ? fmtK(p.Total_Revenue) : "—"}</div>
          {/* Costs */}
          <div style={{ width: SCORECARD_COLS[5].w, flexShrink: 0, fontFamily: mono, fontSize: "11px", color: t.black }}>{n(p.Total_Costs) ? fmtK(p.Total_Costs) : "—"}</div>
          {/* Processing Fees */}
          <div style={{ width: SCORECARD_COLS[6].w, flexShrink: 0, fontFamily: mono, fontSize: "11px", color: procFees !== null && procFees !== 0 ? t.orange : t.muted }}>
            {procFees !== null && procFees !== 0 ? fmtK(Math.abs(procFees)) : "—"}
          </div>
          {/* Proj. Margin */}
          <div style={{ width: SCORECARD_COLS[7].w, flexShrink: 0, fontFamily: mono, fontSize: "11px", color: n(p.Projected_Margin) < 0 ? t.orange : n(p.Projected_Margin) < 2000 ? t.orange : t.green, fontWeight: n(p.Projected_Margin) < 2000 ? 700 : 400 }}>
            {n(p.Total_Costs) > 0 && p.Projected_Margin ? fmtK(p.Projected_Margin) : "—"}
          </div>
          {/* Act. Margin */}
          <div style={{ width: SCORECARD_COLS[8].w, flexShrink: 0, fontFamily: mono, fontSize: "11px", color: actualMargin === null ? t.muted : actualMargin < 0 ? t.orange : actualMargin < 2000 ? t.orange : t.green, fontWeight: actualMargin !== null ? 700 : 400 }}>
            {actualMargin !== null ? fmtK(actualMargin) : "—"}
          </div>
          {/* Status */}
          <div style={{ width: SCORECARD_COLS[9].w, flexShrink: 0, overflow: "hidden" }}>
            <span style={{ fontFamily: mono, fontSize: "8px", letterSpacing: "0.08em", padding: "2px 6px", border: `1px solid ${statusCol}`, color: statusCol }}>{status || "—"}</span>
          </div>
          {/* Outstanding */}
          <div style={{ width: SCORECARD_COLS[10].w, flexShrink: 0, fontFamily: mono, fontSize: "11px", color: n(p.Outstanding_Balance) > 0 ? t.black : t.green }}>
            {!n(p.Package_Price) ? <span style={{ color: t.muted }}>—</span> : n(p.Outstanding_Balance) > 0 ? fmtK(p.Outstanding_Balance) : <span style={{ fontFamily: mono, fontSize: "8px", letterSpacing: "0.08em", padding: "2px 6px", border: `1px solid ${t.green}`, color: t.green }}>Paid</span>}
          </div>
        </div>
      );
    });

  // ─── SCORECARD FOOTER BUILDER ──────────────────────────────────────────────

  const buildScorecardFooter = (rows, label) => {
    const pkgTotal    = rows.reduce((s, p) => s + n(p.Package_Price), 0);
    const colTotal    = rows.reduce((s, p) => s + n(p.Total_Revenue), 0);
    const costTotal   = rows.reduce((s, p) => s + n(p.Total_Costs), 0);
    const feesTotal   = rows.reduce((s, p) => s + Math.abs(n(p.Processing_Fees || 0)), 0);
    const projTotal   = rows.filter(p => n(p.Total_Costs) > 0).reduce((s, p) => s + n(p.Projected_Margin), 0);
    const actTotal    = rows.filter(p => p.Actual_Margin !== "" && p.Actual_Margin !== undefined).reduce((s, p) => s + n(p.Actual_Margin), 0);
    const outTotal    = rows.reduce((s, p) => s + n(p.Outstanding_Balance), 0);
    return (
      <div style={{ display: "flex", padding: "13px 16px" }}>
        <div style={{ width: SCORECARD_COLS[0].w, flexShrink: 0, fontFamily: mono, fontSize: "10px", color: t.black, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>{label}</div>
        <div style={{ width: SCORECARD_COLS[1].w, flexShrink: 0 }} />
        <div style={{ width: SCORECARD_COLS[2].w, flexShrink: 0 }} />
        <div style={{ width: SCORECARD_COLS[3].w, flexShrink: 0, fontFamily: mono, fontSize: "12px", fontWeight: 700, color: t.black }}>{fmtK(pkgTotal)}</div>
        <div style={{ width: SCORECARD_COLS[4].w, flexShrink: 0, fontFamily: mono, fontSize: "12px", fontWeight: 700, color: t.black }}>{fmtK(colTotal)}</div>
        <div style={{ width: SCORECARD_COLS[5].w, flexShrink: 0, fontFamily: mono, fontSize: "12px", fontWeight: 700, color: t.black }}>{fmtK(costTotal)}</div>
        <div style={{ width: SCORECARD_COLS[6].w, flexShrink: 0, fontFamily: mono, fontSize: "12px", fontWeight: 700, color: t.orange }}>{feesTotal ? fmtK(feesTotal) : "—"}</div>
        <div style={{ width: SCORECARD_COLS[7].w, flexShrink: 0, fontFamily: mono, fontSize: "12px", fontWeight: 700, color: t.black }}>{fmtK(projTotal)}</div>
        <div style={{ width: SCORECARD_COLS[8].w, flexShrink: 0, fontFamily: mono, fontSize: "12px", fontWeight: 700, color: t.black }}>{actTotal ? fmtK(actTotal) : "—"}</div>
        <div style={{ width: SCORECARD_COLS[9].w, flexShrink: 0 }} />
        <div style={{ width: SCORECARD_COLS[10].w, flexShrink: 0, fontFamily: mono, fontSize: "12px", fontWeight: 700, color: t.black }}>{fmtK(outTotal)}</div>
      </div>
    );
  };

  // ─── COST ROWS (normal mode) ───────────────────────────────────────────────

  const costRows = sortedCosts.map((c, i) => {
    const vendor = (c.Entity || "").toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    return (
      <div key={`cost-${i}`}
        onMouseEnter={() => setHoveredRow(`cost-${i}`)}
        onMouseLeave={() => setHoveredRow(null)}
        style={{ display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: `1px solid ${t.faint}`, borderLeft: `4px solid ${t.faint}`, background: hoveredRow === `cost-${i}` ? t.bgCard : i % 2 === 0 ? t.bg : "transparent", transition: "background 0.15s" }}
      >
        <div style={{ width: COST_COLS[0].w, flexShrink: 0, fontFamily: sans, fontSize: "11px", fontWeight: 600, color: t.black, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "8px" }}>{c.Project_Key || "—"}</div>
        <div style={{ width: COST_COLS[1].w, flexShrink: 0, fontFamily: mono, fontSize: "10px", color: t.black, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "8px" }}>{vendor}</div>
        <div style={{ width: COST_COLS[2].w, flexShrink: 0, fontFamily: mono, fontSize: "10px", color: t.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "8px" }}>{c.Description || ""}</div>
        <div style={{ width: COST_COLS[3].w, flexShrink: 0, fontFamily: mono, fontSize: "10px", color: t.black }}>{c.TxnDate || ""}</div>
        <div style={{ width: COST_COLS[4].w, flexShrink: 0 }}>
          {c.Payment_Status
            ? <span style={{ fontFamily: mono, fontSize: "8px", letterSpacing: "0.08em", padding: "2px 6px", border: `1px solid ${c.Payment_Status === "Paid" ? t.green : t.orange}`, color: c.Payment_Status === "Paid" ? t.green : t.orange }}>{c.Payment_Status}</span>
            : <span style={{ fontFamily: mono, fontSize: "10px", color: t.muted }}>—</span>
          }
        </div>
        <div style={{ width: COST_COLS[5].w, flexShrink: 0, fontFamily: mono, fontSize: "10px", color: t.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "8px" }}>{c.Account_Name || ""}</div>
        <div style={{ width: COST_COLS[6].w, flexShrink: 0, fontFamily: mono, fontSize: "11px", color: t.black, fontWeight: 500 }}>{c.Amount ? fmtFull(c.Amount) : "—"}</div>
      </div>
    );
  });

  const costFooter = (
    <div style={{ display: "flex", justifyContent: "flex-end", padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
        <span style={{ fontFamily: mono, fontSize: "9px", color: t.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>Total</span>
        <span style={{ fontFamily: mono, fontSize: "16px", color: t.black, fontWeight: 500 }}>{fmtFull(sortedCosts.reduce((s, c) => s + n(c.Amount || 0), 0))}</span>
      </div>
    </div>
  );

  // ─── RECONCILE GROUPED ROWS ────────────────────────────────────────────────

  const reconcileRows = accountGroups.flatMap(([acctName, rows]) => {
    const groupTotal      = rows.reduce((s, c) => s + n(c.Amount || 0), 0);
    const groupReconciled = rows.filter(c => reconciled.has(rowId(c))).reduce((s, c) => s + n(c.Amount || 0), 0);
    const allDone         = rows.every(c => reconciled.has(rowId(c)));
    const noneDone        = rows.every(c => !reconciled.has(rowId(c)));

    const headerRow = (
      <div key={`grp-${acctName}`} style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 16px",
        background: allDone ? `${t.green}18` : noneDone ? t.bgCard : `${t.orange}10`,
        borderBottom: `1px solid ${t.faint}`,
        borderLeft: `4px solid ${allDone ? t.green : noneDone ? t.faint : t.orange}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.12em", textTransform: "uppercase", color: allDone ? t.green : t.black, fontWeight: 700 }}>{acctName}</span>
          <span style={{ fontFamily: mono, fontSize: "8px", color: t.muted }}>{rows.length} item{rows.length !== 1 ? "s" : ""}</span>
          {allDone && <span style={{ fontFamily: mono, fontSize: "8px", letterSpacing: "0.08em", padding: "1px 6px", border: `1px solid ${t.green}`, color: t.green }}>Done</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {!allDone && groupReconciled > 0 && (
            <span style={{ fontFamily: mono, fontSize: "9px", color: t.green }}>{fmtFull(groupReconciled)} reconciled</span>
          )}
          <span style={{ fontFamily: mono, fontSize: "11px", fontWeight: 700, color: allDone ? t.green : t.black }}>{fmtFull(groupTotal)}</span>
        </div>
      </div>
    );

    const itemRows = rows.map((c, i) => {
      const id = rowId(c);
      const isReconciled = reconciled.has(id);
      const vendor = (c.Entity || "").toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
      return (
        <div key={`rec-${acctName}-${i}`}
          onClick={() => toggleReconciled(id)}
          style={{
            display: "flex", alignItems: "center", padding: "10px 16px 10px 24px",
            borderBottom: `1px solid ${t.faint}`,
            borderLeft: `4px solid ${isReconciled ? t.green : t.orange}`,
            background: isReconciled ? `${t.green}10` : hoveredRow === `rec-${acctName}-${i}` ? `${t.orange}08` : i % 2 === 0 ? t.bg : "transparent",
            opacity: isReconciled ? 0.5 : 1,
            transition: "background 0.15s, opacity 0.2s",
            cursor: "pointer",
          }}
          onMouseEnter={() => setHoveredRow(`rec-${acctName}-${i}`)}
          onMouseLeave={() => setHoveredRow(null)}
        >
          <div style={{ width: "20px", flexShrink: 0, marginRight: "10px" }}>
            <div style={{ width: "14px", height: "14px", border: `1.5px solid ${isReconciled ? t.green : t.orange}`, background: isReconciled ? t.green : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
              {isReconciled && <svg width="8" height="6" viewBox="0 0 8 6" fill="none"><path d="M1 3L3 5L7 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            </div>
          </div>
          <div style={{ width: COST_COLS[0].w, flexShrink: 0, fontFamily: sans, fontSize: "11px", fontWeight: 600, color: t.black, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "8px" }}>{c.Project_Key || "—"}</div>
          <div style={{ width: COST_COLS[1].w, flexShrink: 0, fontFamily: mono, fontSize: "10px", color: t.black, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "8px" }}>{vendor}</div>
          <div style={{ width: COST_COLS[2].w, flexShrink: 0, fontFamily: mono, fontSize: "10px", color: t.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "8px" }}>{c.Description || ""}</div>
          <div style={{ width: COST_COLS[3].w, flexShrink: 0, fontFamily: mono, fontSize: "10px", color: t.black }}>{c.TxnDate || ""}</div>
          <div style={{ width: COST_COLS[4].w, flexShrink: 0 }}>
            {c.Payment_Status
              ? <span style={{ fontFamily: mono, fontSize: "8px", letterSpacing: "0.08em", padding: "2px 6px", border: `1px solid ${c.Payment_Status === "Paid" ? t.green : t.orange}`, color: c.Payment_Status === "Paid" ? t.green : t.orange }}>{c.Payment_Status}</span>
              : <span style={{ fontFamily: mono, fontSize: "10px", color: t.muted }}>—</span>
            }
          </div>
          <div style={{ width: COST_COLS[5].w, flexShrink: 0, fontFamily: mono, fontSize: "10px", color: t.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "8px" }}>{c.Account_Name || ""}</div>
          <div style={{ width: COST_COLS[6].w, flexShrink: 0, fontFamily: mono, fontSize: "11px", color: isReconciled ? t.green : t.black, fontWeight: 500 }}>{c.Amount ? fmtFull(c.Amount) : "—"}</div>
        </div>
      );
    });

    return [headerRow, ...itemRows];
  });

  // ─── DESKTOP RENDER ────────────────────────────────────────────────────────

  return (
    <div style={{ background: t.bg, minHeight: "100vh", fontFamily: sans, transition: "background 0.2s", animation: "fadeIn 0.6s ease-in", overflowX: "hidden" }}>
      <style>{FONTS}</style>

      <nav style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 48px", borderBottom: `1px solid ${t.faint}`, background: t.bg, position: "relative" }}>
        <Logo t={t} size={29} />
        <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", display: "flex", gap: "2px", background: t.bgCard, padding: "3px" }}>
          {[["overview", "Event Overview"], ["costs", "Project Costs"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", padding: "7px 18px", border: "none", cursor: "pointer", transition: "all 0.15s", background: tab === k ? t.orange : "transparent", color: tab === k ? "#fff" : t.black }}>{l}</button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "3px" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
              <span style={{ fontFamily: mono, fontSize: "11px", color: t.black, fontWeight: 500 }}>{CONFIG.CLIENT.name}</span>
              <span style={{ fontFamily: mono, fontSize: "8px", color: t.muted }}>FY{CURRENT_YEAR}</span>
            </div>
            <span style={{ fontFamily: mono, fontSize: "8px", color: t.muted, opacity: 0.7 }}>
              Synced · {(() => { const d = new Date(); d.setMinutes(0, 0, 0); return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); })()} · auto-syncs hourly
            </span>
          </div>
          <button onClick={() => setDark(d => !d)} style={{ width: "38px", height: "20px", background: dark ? t.orange : t.faint, border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
            <div style={{ position: "absolute", top: "3px", width: "14px", height: "14px", left: dark ? "21px" : "3px", transition: "left 0.2s", background: dark ? "#fff" : t.black }} />
          </button>
          <button onClick={signOut}
            style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", padding: "7px 16px", background: "transparent", color: t.muted, border: `1px solid ${t.faint}`, cursor: "pointer", transition: "border-color 0.15s, color 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = t.muted; e.currentTarget.style.color = t.black; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = t.faint; e.currentTarget.style.color = t.muted; }}>
            Sign Out
          </button>
        </div>
      </nav>

      <div style={{ padding: "40px 48px 64px" }}>

        {tab === "overview" && <>

          {/* ── KPI ROW ── */}
          <div style={{ display: "flex", gap: "2px", marginBottom: "48px" }}>
            <KPICard t={t} label="Packages Sold"        value={fmtK(kpis.packagesSold)}      sub={`${kpis.totalEvents} events`} />
            <KPICard t={t} label="Revenue Collected"     value={fmtK(kpis.totalCollected)}    sub="total collected to date" positive={true} />
            <KPICard t={t} label="Proj. Margin"          value={fmtK(kpis.projMarginTotal)}   sub="across upcoming events" positive={true} />
            <KPICard t={t} label="Lower Expected Margin" value={kpis.eventsAtRisk}            sub="upcoming below $2K margin" alert={kpis.eventsAtRisk > 0} />
            <NextEventCard t={t} events={kpis.nextEvents || []} nextDays={kpis.nextDays} />
          </div>

          {/* ── UPCOMING EVENTS TABLE ── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
            <SLabel t={t} noMargin>Upcoming Events · {upcomingEvents.length}</SLabel>
            <ResetSort show={sSortCol !== "Days_To_Event"} onClick={resetScoreSort} t={t} />
          </div>
          <ScrollTable
            headers={SCORECARD_COLS}
            rows={buildScorecardRows(upcomingEvents, "up")}
            footer={buildScorecardFooter(upcomingEvents, "Subtotal")}
            t={t} maxHeight="45vh" sortCol={sSortCol} sortDir={sSortDir} onSort={handleScoreSort} dark={dark}
          />

          {/* ── COMPLETED EVENTS TABLE ── */}
          {completedEvents.length > 0 && (
            <div style={{ marginTop: "40px" }}>
              <div
                onClick={() => setShowCompleted(s => !s)}
                style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: showCompleted ? "14px" : 0, cursor: "pointer", userSelect: "none" }}
              >
                <span style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.14em", color: t.muted, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                  Completed Events · {completedEvents.length}
                </span>
                <div style={{ flex: 1, height: "1px", background: t.faint }} />
                <span style={{ fontFamily: mono, fontSize: "9px", color: t.muted, transform: showCompleted ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", lineHeight: 1 }}>▼</span>
              </div>
              {showCompleted && (
                <ScrollTable
                  headers={SCORECARD_COLS}
                  rows={buildScorecardRows(completedEvents, "done", true)}
                  footer={buildScorecardFooter(completedEvents, "Subtotal")}
                  t={t} maxHeight="45vh" sortCol={sSortCol} sortDir={sSortDir} onSort={handleScoreSort} dark={dark}
                />
              )}
            </div>
          )}

          {/* ── 60-DAY MARGIN WATCH ── */}
          <div style={{ marginTop: "52px" }}>
            <div style={{ marginBottom: "14px" }}>
              <SLabel t={t} noMargin>60-Day Margin Watch</SLabel>
              <div style={{ display: "flex", gap: "20px", alignItems: "center", marginTop: "10px" }}>
                {[
                  { color: t.green,  label: "Collected" },
                  { color: t.orange, label: "Costs" },
                  { color: t.black,  label: "Proj. Margin" },
                  { dot: true,       label: "Outstanding" },
                ].map(({ color, dot, label }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <div style={{ width: "14px", height: "10px", flexShrink: 0, background: dot ? undefined : color, backgroundImage: dot ? `radial-gradient(circle, ${t.orange} 1.5px, transparent 1.5px)` : undefined, backgroundSize: dot ? "6px 6px" : undefined }} />
                    <span style={{ fontFamily: mono, fontSize: "9px", color: t.muted, letterSpacing: "0.08em" }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            {events60.length === 0 ? (
              <div style={{ fontFamily: mono, fontSize: "10px", color: t.muted, letterSpacing: "0.08em", padding: "24px 0" }}>No events within 60 days.</div>
            ) : (
              events60.map((p, i) => <MarginBar key={i} p={p} t={t} />)
            )}
          </div>
        </>}

        {tab === "costs" && <>

          {reconcileMode && (
            <div style={{ display: "flex", gap: "2px", marginBottom: "32px", animation: "slideIn 0.25s ease-out" }}>
              <div style={{ flex: 2, minWidth: 0, background: t.bgCard, display: "flex", flexDirection: "column" }}>
                <div style={{ height: "3px", background: t.faint, position: "relative" }}>
                  <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${progressPct}%`, background: t.green, transition: "width 0.4s ease" }} />
                </div>
                <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.13em", textTransform: "uppercase", color: t.muted }}>Reconcile Progress</div>
                  <div style={{ fontFamily: mono, fontSize: "22px", fontWeight: 500, color: t.black, letterSpacing: "-0.03em", lineHeight: 1 }}>{reconciledCount} <span style={{ fontSize: "13px", color: t.muted }}>of {sortedCosts.length}</span></div>
                  <div style={{ fontFamily: mono, fontSize: "9px", color: t.muted }}>{progressPct.toFixed(0)}% by value reconciled</div>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0, background: t.green, display: "flex", flexDirection: "column" }}>
                <div style={{ height: "3px", background: "rgba(255,255,255,0.3)" }} />
                <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.13em", textTransform: "uppercase", color: "rgba(255,255,255,0.75)" }}>Reconciled</div>
                  <div style={{ fontFamily: mono, fontSize: "22px", fontWeight: 500, color: "#fff", letterSpacing: "-0.03em", lineHeight: 1 }}>{fmtK(reconciledTotal)}</div>
                  <div style={{ fontFamily: mono, fontSize: "9px", color: "rgba(255,255,255,0.75)" }}>{reconciledCount} item{reconciledCount !== 1 ? "s" : ""} checked off</div>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0, background: t.orange, display: "flex", flexDirection: "column" }}>
                <div style={{ height: "3px", background: "rgba(255,255,255,0.3)" }} />
                <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.13em", textTransform: "uppercase", color: "rgba(255,255,255,0.75)" }}>Remaining</div>
                  <div style={{ fontFamily: mono, fontSize: "22px", fontWeight: 500, color: "#fff", letterSpacing: "-0.03em", lineHeight: 1 }}>{fmtK(unreconciledTotal)}</div>
                  <div style={{ fontFamily: mono, fontSize: "9px", color: "rgba(255,255,255,0.75)" }}>{sortedCosts.length - reconciledCount} item{sortedCosts.length - reconciledCount !== 1 ? "s" : ""} left</div>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0, background: t.bgCard, display: "flex", flexDirection: "column" }}>
                <div style={{ height: "3px", background: t.bgCard }} />
                <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.13em", textTransform: "uppercase", color: t.muted }}>Total</div>
                  <div style={{ fontFamily: mono, fontSize: "22px", fontWeight: 500, color: t.black, letterSpacing: "-0.03em", lineHeight: 1 }}>{fmtK(totalCostAmt)}</div>
                  <div style={{ fontFamily: mono, fontSize: "9px", color: t.muted }}>{projFilter ? projFilter : "all projects"}</div>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Sel t={t} value={projFilter} onChange={setProjFilter} options={projOptions} placeholder="All Projects" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendor, description..."
                style={{ fontFamily: mono, fontSize: "10px", letterSpacing: "0.06em", color: t.text, background: t.bgCard, border: `1px solid ${t.faint}`, padding: "8px 14px", outline: "none", minWidth: "240px" }} />
              {(projFilter || search) && (
                <button onClick={() => { setProjFilter(""); setSearch(""); }} style={{ fontFamily: mono, fontSize: "9px", color: t.muted, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", letterSpacing: "0.08em", textTransform: "uppercase" }}>Clear</button>
              )}
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                onClick={() => setReconcileMode(m => !m)}
                style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", padding: "9px 18px", border: reconcileMode ? "none" : `1px solid ${t.faint}`, cursor: "pointer", transition: "all 0.2s", background: reconcileMode ? t.orange : "transparent", color: reconcileMode ? "#fff" : t.black }}
              >
                {reconcileMode ? "✓ Exit Reconcile" : "Reconcile Mode"}
              </button>
              {reconcileMode && reconciled.size > 0 && (
                <button onClick={() => { setReconciled(new Set()); localStorage.removeItem("ikifi_tw_reconciled"); }} style={{ fontFamily: mono, fontSize: "9px", color: t.muted, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", letterSpacing: "0.08em", textTransform: "uppercase" }}>Clear All</button>
              )}
              <button onClick={exportCSV} style={{ fontFamily: mono, fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", padding: "9px 22px", background: t.black, color: dark ? t.bg : "#fff", border: "none", cursor: "pointer" }}>Export CSV</button>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
            <SLabel t={t} noMargin>
              {sortedCosts.length} line items{projFilter ? ` · ${projFilter}` : ""}
              {reconcileMode ? " · Grouped by account · Click to reconcile" : ""}
            </SLabel>
            {!reconcileMode && <ResetSort show={cSortCol !== "TxnDate" || cSortDir !== "desc"} onClick={resetCostSort} t={t} />}
          </div>

          {reconcileMode ? (
            <div style={{ border: `1px solid ${t.faint}`, overflow: "hidden" }}>
              <div style={{ display: "flex", padding: "11px 16px", background: dark ? "#1E1E1C" : t.black, borderBottom: `1px solid ${t.faint}`, borderLeft: "4px solid transparent" }}>
                <div style={{ width: "30px", flexShrink: 0 }} />
                {COST_COLS.map((h, i) => (
                  <div key={i} style={{ width: h.w, flexShrink: 0, fontFamily: mono, fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", color: "#9B9891", userSelect: "none" }}>{h.label}</div>
                ))}
              </div>
              <div style={{ maxHeight: "70vh", overflowY: "auto" }}>{reconcileRows}</div>
            </div>
          ) : (
            <ScrollTable headers={COST_COLS} rows={costRows} footer={costFooter} t={t} maxHeight="70vh" sortCol={cSortCol} sortDir={cSortDir} onSort={handleCostSort} dark={dark} />
          )}
        </>}

        <div style={{ marginTop: "60px", paddingTop: "20px", borderTop: `1px solid ${t.faint}`, display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontFamily: mono, fontSize: "9px", color: t.muted }}>© ikifi {CURRENT_YEAR}</span>
          <span style={{ fontFamily: mono, fontSize: "9px", color: t.muted }}>Clarity, by design.</span>
        </div>
      </div>
    </div>
  );
}